import React, { useState } from 'react';
import { X, Warning, CurrencyDollar, Clock, ChartLineUp, Lightning, Sparkle, ArrowRight } from '@phosphor-icons/react';

/**
 * Cancellation reason micro-survey.
 * Shown when a user clicks "Cancel" on their subscription. Captures *why*
 * so the admin can analyze churn patterns.
 *
 * Props:
 *   open       — boolean
 *   onClose    — function (called on dismiss without confirming)
 *   onConfirm  — async function ({reason_code, feedback}) → caller actually performs the cancel
 *   busy       — boolean, disables confirm during request
 *   planName   — string, current plan name for the banner
 */
const REASONS = [
  { code: 'too_expensive',     label: 'Too expensive',           icon: CurrencyDollar, color: 'text-red-500' },
  { code: 'not_using',         label: "I'm not using it enough", icon: Clock,          color: 'text-amber-500' },
  { code: 'found_alternative', label: 'Found a better alternative', icon: ChartLineUp, color: 'text-purple-500' },
  { code: 'missing_feature',   label: 'Missing a key feature',   icon: Lightning,      color: 'text-[#2B4C6F]' },
  { code: 'temporary_pause',   label: 'Just need a temporary pause', icon: Sparkle,    color: 'text-emerald-500' },
  { code: 'other',             label: 'Other',                   icon: ArrowRight,     color: 'text-neutral-500' },
];

export default function CancellationReasonModal({ open, onClose, onConfirm, busy = false, planName = 'your plan' }) {
  const [reasonCode, setReasonCode] = useState(null);
  const [feedback, setFeedback] = useState('');

  if (!open) return null;

  const reset = () => { setReasonCode(null); setFeedback(''); };

  const handleConfirm = async () => {
    await onConfirm({ reason_code: reasonCode, feedback: feedback.trim() });
    reset();
  };

  const handleClose = () => { reset(); onClose(); };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4"
      onClick={handleClose}
      data-testid="cancel-reason-modal"
    >
      <div
        className="bg-white dark:bg-neutral-800 border-4 border-black rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-[8px_8px_0px_#D95800]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center border-4 border-black shadow-[3px_3px_0px_#000] mb-2">
              <Warning size={22} weight="fill" className="text-white" />
            </div>
            <h2 className="text-xl font-black text-neutral-900 dark:text-white">Before you go...</h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">Help us improve {planName} — what's the reason?</p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center hover:bg-neutral-200 dark:hover:bg-neutral-600"
            data-testid="cancel-reason-close"
            aria-label="Close"
          ><X size={14} weight="bold" /></button>
        </div>

        <div className="space-y-2 mb-4">
          {REASONS.map((r) => (
            <button
              key={r.code}
              type="button"
              onClick={() => setReasonCode(r.code)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all ${
                reasonCode === r.code
                  ? 'border-[#D95800] bg-[#D95800]/10 shadow-[2px_2px_0px_#D95800]'
                  : 'border-neutral-200 dark:border-neutral-600 hover:border-neutral-400'
              }`}
              data-testid={`reason-${r.code}`}
            >
              <r.icon size={18} weight="bold" className={r.color} />
              <span className="font-bold text-sm text-neutral-900 dark:text-white">{r.label}</span>
            </button>
          ))}
        </div>

        <label className="block text-xs font-black uppercase text-neutral-500 dark:text-neutral-400 mb-1">
          Anything else? <span className="font-normal normal-case text-neutral-400">(optional)</span>
        </label>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Tell us what we could do better..."
          className="w-full px-3 py-2 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white resize-none mb-1"
          data-testid="cancel-feedback-input"
        />
        <p className="text-[10px] text-neutral-400 dark:text-neutral-500 text-right mb-4">{feedback.length}/500</p>

        <div className="flex gap-2">
          <button
            onClick={handleClose}
            disabled={busy}
            className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-50"
            data-testid="cancel-keep-btn"
          >Keep My Plan</button>
          <button
            onClick={handleConfirm}
            disabled={busy}
            className="flex-1 h-11 bg-white dark:bg-neutral-700 text-red-600 dark:text-red-400 font-bold rounded-lg border-2 border-red-500 transition-all disabled:opacity-50"
            data-testid="cancel-confirm-btn"
          >{busy ? 'Cancelling...' : 'Confirm Cancel'}</button>
        </div>

        <p className="text-[10px] text-neutral-400 dark:text-neutral-500 text-center mt-3">
          You'll keep access until the end of your current period.
        </p>
      </div>
    </div>
  );
}
